import { ref } from "vue";
import { defineStore } from "pinia";
import calculateRate from "@/utils/calculateRate";

export const useP2PStore = defineStore("p2p", () => {
  const P2P2_ERROR_CODES = [
    "P2P2-9000",
    "P2P2-0001",
    "P2P2-0002",
    "P2P2-0003",
    "P2P2-0004",
    "P2P2-0005",
    "P2P2-0006",
    "P2P2-0007",
    "P2P2-0008",
    "P2P2-0009",
    "P2P2-0010",
    "P2P2-0011",
    "P2P2-0012",
    "P2P2-0013",
    "P2P2-0014",
    "P2P2-0015",
    "P2P2-0016",
    "P2P2-0017",
    "P2P2-0019",
    "P2P2-0020",
    "P2P2-0021",
    "P2P2-0023",
    "P2P2-0026",
    "P2P2-0027",
    "P2P2-0028",
    "P2P2-0132",
    "2fa-0008",
    "CHAT-0004",
  ];

  const TYPE_RATE = {
    fixed: "FIXED_RATE",
    dynamic: "DYNAMIC_PERCENT_RATE",
  };

  const PAYMENTS_TYPE = {
    BANK_CARD: "bank_card",
    BANK_TRANSFER: "bank_transfer",
    FREE_FIELD: "free_field",
    SBP: "sbp",
  };

  const CANCELED_REASON = {
    TIMEOUT: "cancel_due_to_timeout",
    BUYER_WANTS: "buyer_wants_cancel",
  };

  const ORDER_STATUSES = {
    WAIT_CRYPTO_HOLD: {
      key: "wait_crypto_hold",
      type: "process",
      text: "Order status | Wait crypto hold",
      description: "Order status | Wait crypto hold description",
    },
    WAIT_FIAT_TRANSFER: {
      key: "wait_fiat_transfer",
      type: "process",
      text: "Order status | Wait fiat transfer",
      description: "Order status | Wait fiat transfer description",
    },
    WAIT_FIAT_RECEIVED: {
      key: "wait_fiat_received",
      type: "process",
      text: "Order status | Wait fiat received",
      description: "Order status | Wait fiat received description",
    },
    WAIT_CRYPTO_TRANSFER: {
      key: "wait_crypto_transfer",
      type: "process",
      text: "Order status | Wait crypto transfer",
      description: "Order status | Wait crypto transfer description",
    },
    DONE: {
      key: "done",
      type: "success",
      text: "Order status | Done",
      description: "Order status | Done description",
    },
    ERROR: {
      key: "error",
      type: "error",
      text: "Order status | Error",
      description: "Order status | Error description",
    },
    CANCELED: {
      key: "canceled",
      type: "error",
      text: "Order status | Canceled",
      description: "Order status | Canceled description",
    },
    DISPUTE: {
      key: "dispute",
      type: "error",
      text: "Order status | Dispute",
      description: "Order status | Dispute description",
    },
  };

  const fiatCurrencies = ref([]);
  const cryptoCurrencies = ref([]);
  const paymentMethods = ref([]);
  const p2pRates = ref({});
  const errorCode = ref("");

  const setFiatCurrencies = (value) => {
    fiatCurrencies.value = value;
  };
  const setCryptoCurrencies = (value) => {
    cryptoCurrencies.value = value.map((item) =>
      item?.code === "ton" ? { ...item, name: "GRAM" } : item,
    );
  };
  const setPaymentMethods = (value) => {
    paymentMethods.value = value;
  };
  const setP2PRates = (value) => {
    p2pRates.value = value;
  };
  const setNewErrorCode = (value) => {
    errorCode.value = value;
  };
  const clearErrorCode = () => {
    errorCode.value = "";
  };

  const cryptoCurrenciesByCode = (code) =>
    cryptoCurrencies.value?.find((item) => item?.code === code);
  const fiatCurrenciesByCode = (code) =>
    fiatCurrencies.value?.find((item) => item?.code === code);

  const getFilteredPaymentMethods = (fiatCurrency) => {
    if (!paymentMethods.value || !paymentMethods.value.length) {
      return [];
    }

    return paymentMethods.value
      .filter((item) =>
        item.currencies?.some((currency) => currency.code === fiatCurrency),
      )
      .map((item) => {
        const currency = item.currencies.find(
          (curr) => curr.code === fiatCurrency,
        );

        return {
          code: item.code,
          name: item.name,
          key: item.code,
          type: currency?.pm_types?.[0] || "",
          counter_details: "",
        };
      });
  };

  const getP2Prate = (
    cryptoCurrency,
    fiatCurrency,
    percent,
    needFormat = true,
  ) =>
    calculateRate(
      p2pRates.value[
        `${cryptoCurrency.toUpperCase()}_${fiatCurrency.toUpperCase()}`
      ],
      +percent,
      needFormat,
    );

  return {
    setFiatCurrencies,
    setCryptoCurrencies,
    setPaymentMethods,
    setP2PRates,
    getFilteredPaymentMethods,
    cryptoCurrencies,
    fiatCurrencies,
    paymentMethods,
    cryptoCurrenciesByCode,
    fiatCurrenciesByCode,
    P2P2_ERROR_CODES,
    TYPE_RATE,
    ORDER_STATUSES,
    PAYMENTS_TYPE,
    getP2Prate,
    errorCode,
    setNewErrorCode,
    clearErrorCode,
    CANCELED_REASON,
  };
});
